import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ['translateButton', 'statusMessage']
  static values = {
    apiUrl: String,
    availableLocales: Array
  }

  connect() {
    this.translating = false
  }

  async translateFields(event) {
    event.preventDefault()

    if (this.translating) {
      this.showStatus('Překlad již probíhá...', 'warning')
      return
    }

    this.translating = true
    this.disableButton()

    try {
      const translateFields = JSON.parse(event.currentTarget.dataset.translateFields || '[]')
      const fieldsToTranslate = this.findFieldsToTranslate(translateFields)

      if (fieldsToTranslate.length === 0) {
        this.showStatus('Všechna pole jsou již vyplněna', 'info')
        return
      }

      this.showStatus(`Překládám ${fieldsToTranslate.length} polí...`, 'info')

      for (const field of fieldsToTranslate) {
        await this.translateField(field)
      }

      this.showStatus('Překlad dokončen', 'success')
    } catch (error) {
      this.showStatus(`Chyba: ${error.message}`, 'error')
      console.error('Translation error:', error)
    } finally {
      this.translating = false
      this.enableButton()
    }
  }

  findFieldsToTranslate(fieldNames) {
    const fields = []
    const form = this.element

    fieldNames.forEach(fieldName => {
      const fieldGroups = {}
      const pattern = new RegExp(`\\w+\\[${fieldName}\\]\\[([a-z]{2})\\]$`)

      const inputs = form.querySelectorAll(`input[name*="[${fieldName}]"], textarea[name*="[${fieldName}]"]`)

      inputs.forEach(input => {
        const match = input.name.match(pattern)
        if (match) {
          const locale = match[1]
          const baseName = input.name.replace(pattern, '')

          if (!fieldGroups[baseName]) {
            fieldGroups[baseName] = {}
          }
          fieldGroups[baseName][locale] = input
        }
      })

      Object.entries(fieldGroups).forEach(([baseName, localeInputs]) => {
        const sourceText = this.findSourceText(localeInputs)
        const sourceLocale = this.findSourceLocale(localeInputs)

        if (!sourceText || !sourceLocale) {
          return
        }

        Object.entries(localeInputs).forEach(([locale, input]) => {
          if (locale !== sourceLocale && this.isFieldEmpty(input)) {
            fields.push({
              input: input,
              sourceText: sourceText,
              sourceLocale: sourceLocale,
              targetLocale: locale,
              fieldName: `${baseName}[${fieldName}]`
            })
          }
        })
      })
    })

    return fields
  }

  findSourceText(localeInputs) {
    const priority = ['en', 'cs']

    for (const locale of priority) {
      if (localeInputs[locale] && !this.isFieldEmpty(localeInputs[locale])) {
        return localeInputs[locale].value.trim()
      }
    }

    for (const [locale, input] of Object.entries(localeInputs)) {
      if (!this.isFieldEmpty(input)) {
        return input.value.trim()
      }
    }

    return null
  }

  findSourceLocale(localeInputs) {
    const priority = ['en', 'cs']

    for (const locale of priority) {
      if (localeInputs[locale] && !this.isFieldEmpty(localeInputs[locale])) {
        return locale
      }
    }

    for (const [locale, input] of Object.entries(localeInputs)) {
      if (!this.isFieldEmpty(input)) {
        return locale
      }
    }

    return null
  }

  isFieldEmpty(input) {
    return !input.value || input.value.trim() === ''
  }

  async translateField(field) {
    try {
      const response = await fetch('/api/v1/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          text: field.sourceText,
          source_lang: field.sourceLocale.toUpperCase(),
          target_lang: field.targetLocale.toUpperCase()
        })
      })

      if (response.status === 429) {
        this.showStatus('Rate limit, čekám 15s...', 'warning')
        await this.sleep(15000)
        return this.translateField(field)
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`)
      }

      const data = await response.json()
      field.input.value = data.translated_text
      field.input.dispatchEvent(new Event('input', { bubbles: true }))

      this.showStatus(`Přeloženo: ${field.targetLocale.toUpperCase()} - ${field.fieldName}`, 'success')

      await this.sleep(500)
    } catch (error) {
      console.error(`Failed to translate ${field.fieldName} to ${field.targetLocale}:`, error)
      throw error
    }
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  getAuthToken() {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token') || ''
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  disableButton() {
    if (this.hasTranslateButtonTarget) {
      this.translateButtonTarget.disabled = true
      this.translateButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  enableButton() {
    if (this.hasTranslateButtonTarget) {
      this.translateButtonTarget.disabled = false
      this.translateButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  showStatus(message, type = 'info') {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.className = `status-message status-${type}`
    } else {
      console.log(message)
    }
  }
}
